/**
 * usePlacementAbschluss - TanStack Query hooks for the Ausschreibungs-Screen Tab 6 (Policenkontrolle +
 * Abschluss + Zustellung; P2 Placement Slice 15). live mode hits apiClient.placements.*Abschluss; mock
 * mode resolves a fixture (Helvetia Antrag police_erhalten, a draft police HEL-2027-004711, an aktuell
 * Kontrolle with an open Abweichung on the Selbstbehalt). The server owns every gate; the UI only
 * triggers + renders. While a send is running the query refetches every 5 s.
 *
 * [Source: apps/web/src/features/placements/usePlacementUnderwriting.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import type { PlacementAbschlussResponse, PkPoliceVerknuepfenBody, PkAbweichungGeklaertBody, PkRueckmeldungVersichererBody } from '@brokly/shared';

function mockAbschluss(id: string): PlacementAbschlussResponse {
  return {
    placement_id: id,
    placement_number: 'PL-2026-000512',
    status: 'entschieden',
    closure: {
      status: 'offen',
      start_zeitpunkt: '2026-11-01T08:00:00.000Z',
      end_zeitpunkt: null,
      abschluss_art: null,
      abschluss_art_label: null,
      abgeschlossen_by_name: null,
      comparison_result: 'abweichung',
      comparison_documented_at: '2026-11-20T09:00:00.000Z',
      dauer_tage: null,
    },
    antrag: {
      id: 'app-mock',
      status: 'geprueft',
      status_label: 'Geprueft',
      carrier_uid: 'CHE-101.838.586',
      carrier_name: 'Helvetia Versicherungen AG',
      submission_number: 'SUB-2026-000671-HAFT',
      police_erhalten_policy_number: 'HEL-2027-004711',
      police_erhalten_at: '2026-11-20T08:30:00.000Z',
    },
    police: {
      id: 'pol-mock',
      policy_number: 'HEL-2027-004711',
      status: 'draft',
      status_label: 'Entwurf',
      bezeichnung: 'Betriebshaftpflicht - Helvetia',
      carrier_uid: 'CHE-101.838.586',
      carrier_name: 'Helvetia Versicherungen AG',
      inception_date: '2027-01-01',
      expiry_date: '2027-12-31',
      premium_gross: 4850,
      aktiv: false,
      ersetzt_shell_id: null,
    },
    kontrolle: {
      id: 'ctrl-mock',
      kontroll_nr: 1,
      is_aktuell: true,
      ausgeloest_durch: 'police_erhalten',
      ausgeloest_durch_label: 'Police erhalten (Underwriting)',
      match_art: 'policennummer',
      match_art_label: 'Policennummer aus dem Underwriting',
      result: 'abweichung',
      zeilen: [
        { feld: 'versicherer', label: 'Versicherer', soll: 'CHE-101.838.586', ist: 'CHE-101.838.586', status: 'gleich', toleranz: null, hinweis: null },
        { feld: 'policennummer', label: 'Policennummer', soll: 'HEL-2027-004711', ist: 'HEL-2027-004711', status: 'gleich', toleranz: null, hinweis: null },
        { feld: 'versicherungsbeginn', label: 'Versicherungsbeginn', soll: '2027-01-01', ist: '2027-01-01', status: 'gleich', toleranz: null, hinweis: null },
        { feld: 'vertragsablauf', label: 'Vertragsablauf', soll: '2027-12-31', ist: '2027-12-31', status: 'gleich', toleranz: '+/- 31 Tage', hinweis: null },
        { feld: 'praemie_jahr', label: 'Jahrespraemie', soll: 4850, ist: 4850, status: 'gleich', toleranz: 'max. CHF 5 / 1 %', hinweis: 'Bruttopraemie der Police' },
        { feld: 'versicherungssumme', label: 'Versicherungssumme', soll: 5000000, ist: null, status: 'unbekannt', toleranz: null, hinweis: 'Nicht in der Police hinterlegt' },
        { feld: 'selbstbehalt', label: 'Selbstbehalt', soll: 500, ist: 1000, status: 'abweichend', toleranz: null, hinweis: null },
      ],
      abweichungen_count: 1,
      unbekannt_count: 1,
      vollstaendig: false,
      policy_status_bei_kontrolle: 'draft',
      befund: {
        result: 'abweichung',
        headline: 'Police HEL-2027-004711 weicht vom Antrag ab (1 Feld)',
        abweichungen: ["Abweichung im Bereich Selbstbehalt: Antrag CHF 500, Police CHF 1'000"],
        nicht_pruefbar: ['Versicherungssumme: in der Police nicht pruefbar'],
      },
      rueckmeldung: null,
      klaerung: { status: 'offen', status_label: 'Klaerung offen', task_id: 'task-mock', geklaert_at: null, geklaert_by_name: null, grund: null },
      ausgefuehrt_at: '2026-11-20T09:00:00.000Z',
      ausgefuehrt_by_name: 'Berater B',
    },
    kontrollen: [],
    zustellung: {
      zugestellt_at: null,
      channel: null,
      channel_label: null,
      portal_delivery_id: null,
      communication_id: null,
      task_id: null,
      versand_laeuft: false,
      versand_unterbrochen: false,
      versand_gesperrt: false,
      nachtragen_moeglich: false,
      letzter_fehler: null,
      letzter_fehler_at: null,
      portal_verfuegbar: true,
      kunde_email: 'kunde@mock-firm.ch',
    },
    aktionen: { kontrolle_ausfuehren: true, police_verknuepfen: false, abweichung_klaeren: true, police_zustellen: false, rueckmeldung_versicherer: true },
    rueckmeldung_empfaenger_vorschlag: 'underwriting@helvetia.ch',
    hinweise: [],
  };
}

export function usePlacementAbschluss(id: string, enabled = true) {
  const live = isLiveData();
  return useQuery<PlacementAbschlussResponse>({
    queryKey: qk.placementAbschluss(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getAbschluss(id) : Promise.resolve(mockAbschluss(id))),
    enabled,
    refetchInterval: (query) => (live && query.state.data?.zustellung.versand_laeuft ? 5000 : false),
    ...(live ? {} : { initialData: () => mockAbschluss(id), staleTime: Infinity }),
  });
}

function useAbschlussInvalidate(id: string) {
  const qc = useQueryClient();
  return (commId?: string) => {
    void qc.invalidateQueries({ queryKey: ['placements', 'abschluss', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'historie', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'underwriting', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
    // P2 hardening 05: when the Rueckmeldung was sent from the Maske (threaded onto the Eingang), T5 +
    // the Systemanalyse pane of that Eingang must refetch to show the sent reply + the stamp.
    if (commId) {
      void qc.invalidateQueries({ queryKey: ['posteingang', 'activity', commId] });
      void qc.invalidateQueries({ queryKey: ['posteingang', 'policenkontrolle', commId] });
    }
  };
}

export function useKontrolleAbschluss(id: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.kontrolleAbschluss(id) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(),
  });
}

export function usePoliceVerknuepfenAbschluss(id: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: (body: PkPoliceVerknuepfenBody) => (live ? apiClient.placements.policeVerknuepfenAbschluss(id, body) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(),
  });
}

export function useAbweichungGeklaertAbschluss(id: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: (body: PkAbweichungGeklaertBody) => (live ? apiClient.placements.abweichungGeklaertAbschluss(id, body) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(),
  });
}

export function usePoliceZustellenAbschluss(id: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.policeZustellenAbschluss(id) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(),
  });
}

/** PR #43 wave 2 (#6): resolve an interrupted Police-Zustellung (nachtragen / freigeben). */
export function useZustellungAufloesenAbschluss(id: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: (aktion: 'nachtragen' | 'freigeben') => (live ? apiClient.placements.zustellungAufloesenAbschluss(id, aktion) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(),
  });
}

/**
 * P2 hardening 05: reply to the insurer on an abweichung. `commId` (optional) is the Eingang the reply
 * threads onto - passing it also refetches that Eingang's T5 + Systemanalyse pane on success.
 */
export function useRueckmeldungVersichererAbschluss(id: string, commId?: string) {
  const live = isLiveData();
  const invalidate = useAbschlussInvalidate(id);
  return useMutation({
    mutationFn: (body: PkRueckmeldungVersichererBody) => (live ? apiClient.placements.rueckmeldungVersichererAbschluss(id, body) : Promise.resolve(mockAbschluss(id))),
    onSuccess: () => invalidate(commId),
  });
}
